import { createAsyncThunk } from '@reduxjs/toolkit';
import i18next from 'i18next';

import api from '@setup/api';
import { handleServerError } from '@setup/config/serverErrorHandler';
import { NotificationService } from '@setup/notifications/SnackbarUtilsConfigurator/SnackbarUtilsConfigurator';
import { StatusServerErrors } from '@shared/enums/status-server-errors';
import { type BodyArtModel } from '@shared/models/profileNew/profileNew.model';

import { isEmptyDataFamilyWithFilter, removeCurrentFamily, setLoadingFamily } from './family-slice';

import type {
  BodyArtsCreateFlowToRequest,
  FamilyAppearance,
  FamilyAppearanceToRequest,
  FamilyDataForCreate,
  FamilyDataForUpdate,
  RootFamilyData,
} from './family-types';
import type { RootMeta } from '@pages/hubs/Garage/store/types';
import type { GetHubsParams } from '@pages/hubs/Pets/store/pets-types';
import type { ErrorType, ServerErrorType } from '@shared/interfaces/error.interfaces';
import type { CreateBodyArtFormModel } from '@shared/models/profile/profileBodyArtForm.model';
import type { GetBodyArts } from '@shared/models/profileNew/profileNew.model';
import type { AppDispatch, RootState } from '@store/store';

export const getFamilyList = createAsyncThunk<
  { data: RootFamilyData[]; meta: RootMeta },
  Partial<GetHubsParams>,
  { rejectValue: ErrorType; dispatch: AppDispatch; state: RootState }
>('getFamilyList', async (familyParams, { dispatch, rejectWithValue }) => {
  dispatch(setLoadingFamily({ isLoading: false }));
  try {
    const { data, meta } = await api.family.getFamilyList({
      page: familyParams.page || 1,
      ...familyParams,
    });
    dispatch(isEmptyDataFamilyWithFilter({ filters: familyParams, familyData: data }));

    return {
      data,
      meta,
    };
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  } finally {
    dispatch(setLoadingFamily({ isLoading: true }));
  }
});

export const getFamilyMember = createAsyncThunk<RootFamilyData, number, { rejectValue: ErrorType }>(
  'getFamilyMember',
  async (memberId, { rejectWithValue }) => {
    try {
      return await api.family.getFamilyMember(memberId);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(
        e,
        {
          [StatusServerErrors.AccessDenied]: {
            message: i18next.t('family.notification.memberNotSharedWithYou'),
          },
          [StatusServerErrors.ItemNotFound]: {
            message: i18next.t('family.notification.memberNotFound'),
          },
        },
        {
          notificationVariant: 'info',
        },
      );

      return rejectWithValue(axiosError.data);
    }
  },
);

export const createFamily = createAsyncThunk<RootFamilyData, FamilyDataForCreate, { rejectValue: ErrorType }>(
  'createFamily',
  async (data, { rejectWithValue }) => {
    try {
      return await api.family.createFamily(data);
    } catch (e) {
      const axiosError = e as ServerErrorType;
      handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
      return rejectWithValue(axiosError.data);
    }
  },
);

export const updateFamily = createAsyncThunk<
  RootFamilyData,
  { data: FamilyDataForUpdate; familyId: number },
  { rejectValue: ErrorType }
>('updateFamily', async ({ data, familyId }, { rejectWithValue }) => {
  try {
    return await api.family.updateFamily(data, familyId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });

    return rejectWithValue(axiosError.data as ErrorType);
  }
});

export const createFamilyAppearance = createAsyncThunk<
  FamilyAppearance,
  { data: Partial<FamilyAppearanceToRequest> & { family_member_id: number } },
  { rejectValue: ErrorType }
>('createFamilyAppearance', async ({ data }, { rejectWithValue }) => {
  try {
    return await api.family.createFamilyAppearance(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data as ErrorType);
  }
});

export const updateFamilyAppearance = createAsyncThunk<
  FamilyAppearance,
  { data: Partial<FamilyAppearanceToRequest> & { family_member_id: number }; appearanceId: number },
  { rejectValue: ErrorType }
>('updateFamilyAppearance', async ({ data, appearanceId }, { rejectWithValue }) => {
  try {
    return await api.family.updateFamilyAppearance(data, appearanceId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const createFamilyBodyArtsForCreateFlow = createAsyncThunk<
  Array<BodyArtModel>,
  { data: BodyArtsCreateFlowToRequest },
  { rejectValue: ErrorType }
>('createFamilyAppearance', async ({ data }, { rejectWithValue }) => {
  try {
    return await api.family.createFamilyBodyArtsForCreateFlow(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const deleteFamily = createAsyncThunk<
  any,
  { familyId: number; familyName: string },
  { rejectValue: ErrorType }
>('deleteFamily', async ({ familyId, familyName }, { rejectWithValue, dispatch }) => {
  try {
    return await api.family.deleteFamily(familyId).then(() => {
      dispatch(removeCurrentFamily({ familyId }));
      NotificationService.success(i18next.t('family.notification.removeFamily', { familyName: familyName }));
    });
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});

export const getFamilyBodyArts = createAsyncThunk<
  GetBodyArts,
  number,
  { rejectValue: ErrorType; state: RootState }
>('getFamilyBodyArts', async (memberId, { rejectWithValue, getState }) => {
  try {
    const page = getState().family.bodyArtsMeta.page;

    return await api.family.getFamilyBodyArts(memberId, page);
  } catch (e) {
    const axiosError = e as ServerErrorType;

    handleServerError(
      e,
      {
        [StatusServerErrors.AccessDenied]: {
          message: i18next.t('family.notification.memberNotSharedWithYou'),
        },
        [StatusServerErrors.ItemNotFound]: {
          message: i18next.t('family.notification.memberNotFound'),
        },
      },
      {
        notificationVariant: 'info',
      },
    );
    return rejectWithValue(axiosError.data);
  }
});

export const createFamilyBodyArt = createAsyncThunk<
  BodyArtModel,
  { data: CreateBodyArtFormModel & { family_member_id: number } },
  { rejectValue: ErrorType }
>('createFamilyBodyArt', async ({ data }, { rejectWithValue }) => {
  try {
    return await api.family.createFamilyBodyArt(data);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const updateFamilyBodyArt = createAsyncThunk<
  BodyArtModel,
  { data: CreateBodyArtFormModel & { family_member_id: number }; bodyArtId: number },
  { rejectValue: ErrorType }
>('updateFamilyBodyArt', async ({ data, bodyArtId }, { rejectWithValue }) => {
  try {
    return await api.family.updateFamilyBodyArt(data, bodyArtId);
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e, {}, { notify: axiosError.status !== StatusServerErrors.ValidationException });
    return rejectWithValue(axiosError.data);
  }
});

export const deleteFamilyBodyArt = createAsyncThunk<
  any,
  { family_member_id: number; bodyArtId: number },
  { rejectValue: ErrorType }
>('deleteFamily', async ({ family_member_id, bodyArtId }, { rejectWithValue }) => {
  try {
    return await api.family.deleteFamilyBodyArt(family_member_id, bodyArtId).then(() => {
      NotificationService.success(i18next.t('general.notifications.bodyArtDeleted'));
      return bodyArtId;
    });
  } catch (e) {
    const axiosError = e as ServerErrorType;
    handleServerError(e);
    return rejectWithValue(axiosError.data);
  }
});
